import { apiFetch } from "@/shared/api/apiFetch";
import { readApiErrorMessage } from "@/shared/api/parseApiErrorMessage";

/**
 * Where a published app's draft version stands.
 *
 * While an app is published, App Studio edits it through `draft_app_id`: every builder request
 * addressed at that id changes the draft, never what viewers see, until the draft is published.
 * `draft_app_id` is `null` for an app with no draft (an unpublished app is edited directly).
 */
export type AppDraftState = {
  app_id: number;
  draft_app_id: number | null;
  has_changes: boolean;
};

function isAppDraftState(value: unknown): value is AppDraftState {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    typeof row.app_id === "number" &&
    (typeof row.draft_app_id === "number" || row.draft_app_id === null) &&
    typeof row.has_changes === "boolean"
  );
}

async function requestDraftState(path: string, method: "GET" | "POST"): Promise<AppDraftState> {
  const res = await apiFetch(path, { method });
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
  const data: unknown = await res.json();
  if (!isAppDraftState(data)) {
    throw new Error("Unexpected response shape");
  }
  return data;
}

export function fetchAppDraft(appId: number): Promise<AppDraftState> {
  return requestDraftState(`/v1/apps/${appId}/draft`, "GET");
}

// One request per app at a time: the builder and an "add to app" dialog can both ask for the same
// published app's draft as they mount, and the first request is the one that creates it.
const openRequests = new Map<number, Promise<AppDraftState>>();

/** The published app's draft, created from what is published the first time it is asked for. */
export function openAppDraft(appId: number): Promise<AppDraftState> {
  const inFlight = openRequests.get(appId);
  if (inFlight) {
    return inFlight;
  }
  const request = requestDraftState(`/v1/apps/${appId}/draft`, "POST").finally(() => {
    openRequests.delete(appId);
  });
  openRequests.set(appId, request);
  return request;
}

/** Make the draft what viewers see. The draft stays, now matching the published app. */
export function publishAppDraft(appId: number): Promise<AppDraftState> {
  return requestDraftState(`/v1/apps/${appId}/draft/publish`, "POST");
}

/** Throw away the draft's changes, resetting it to the published version. */
export function discardAppDraft(appId: number): Promise<AppDraftState> {
  return requestDraftState(`/v1/apps/${appId}/draft/discard`, "POST");
}
